import {
  SearchResultsPage,
  SearchResultsPageProps,
} from '@bridebook/web/app-basic/components/search/search-results-page';
import { SearchSuppliersPage } from '@bridebook/web/app-basic/components/search/search-suppliers-page';
import { setAlternateLinks, setCanonicalLink } from '@bridebook/web/app-basic/lib/app/app-slice';
import { setInitialBasicAppState } from '@bridebook/web/app-basic/lib/app/set-initial-basic-app-state';
import { TBasicStore } from '@bridebook/web/app-basic/lib/basic-store';
import {
  EdgeCacheTTL,
  basicGSSPWrapper,
} from '@bridebook/web/app-basic/lib/get-basic-server-side-props-wrapper';
import { basicSearchPageI18nKeyFilter } from '@bridebook/web/app-basic/lib/search/basic-search-page-i18n-key-filter';
import { fetchInitialProps } from '@bridebook/web/app-basic/lib/search/fetch-initial-props';
import { useRouter } from 'next/router';
import type { GetServerSidePropsContext } from 'next/types';
import { mapUrlSlugToSupplierType } from '@bridebook/toolbox/src';
import {
  filterSearchLandingItems,
  getTopCountiesPerCountry,
  getTopTypes,
} from '@bridebook/toolbox/src/search-suppliers/landing-page-search/transform-landing-items';
import type { UrlFriendlySlug } from '@bridebook/toolbox/src/types';
import { getArticlesMarketSupport } from '@bridebook/web/app-shared/lib/articles/get-articles-market-support';
import { SearchSuppliersPageProps } from '@bridebook/web/app-shared/lib/search-suppliers/types';
import { fetchSearchSuppliers } from '@bridebook/web/app-shared/lib/search-suppliers/utils';
import { ISearchQuery } from '@bridebook/web/app-shared/lib/search/types';
import { getSuppliersLandingPageTexts } from '@bridebook/web/app-shared/lib/search/utils';
import { getSearchRedirect } from '@bridebook/web/app-shared/lib/search/utils/redirects';
import { AppType } from '@bridebook/web/pages/_app';
import ErrorPage from '@bridebook/web/pages/_error';
import { isSupplierTypeSupported } from '@bridebook/web/lib/i18n/utils/is-supplier-type-supported';
import {
  getAlternateLinksForSearch,
  getAlternateLinksForSearchLanding,
} from '@bridebook/web/lib/utils/i18n-routing/get-alternate-links';
import { getCanonicalUrl } from '@bridebook/web/lib/utils/url';
import { setArticlesSupport } from '../../../../app-basic/lib/article/article-slice';

interface ErrorPageProps {
  error?: boolean;
  notFound?: boolean;
}

const SearchPage = (props: SearchResultsPageProps | SearchSuppliersPageProps | ErrorPageProps) => {
  const { query } = useRouter();

  if (hasErrorPageProps(props)) return <ErrorPage statusCode={404} />;

  /**
   * Initially we were validating properties returned by getServerSideProps, but
   * those are unreliable, because they are not always present (for example in case of error),
   * so validating params length instead seems like more solid approach.
   */
  const isSearchResultsPage = getIsSearchResultsPage(query.searchParams);

  return isSearchResultsPage ? (
    <SearchResultsPage {...(props as SearchResultsPageProps)} />
  ) : (
    <SearchSuppliersPage {...(props as SearchSuppliersPageProps)} />
  );
};

export const getServerSideProps = basicGSSPWrapper(
  [],
  async ({ store, context, initialAppState }) => {
    const { query } = context;
    const { searchParams } = query;

    const isSearchResultsPage = getIsSearchResultsPage(searchParams);

    if (isSearchResultsPage) {
      return searchResultPageGSSCallback({ searchParams, store, context, initialAppState });
    } else {
      return searchLandingPageGSSCallback({ searchParams, store, context, initialAppState });
    }
  },
  {
    vercelCacheControl: {
      maxAge: EdgeCacheTTL.HOUR,
      sMaxAge: EdgeCacheTTL.HOUR,
      staleWhileRevalidate: EdgeCacheTTL.MONTH,
    },
    cloudflareCacheControl: {
      maxAge: EdgeCacheTTL.TEN_MINUTES,
      sMaxAge: EdgeCacheTTL.TEN_MINUTES,
      staleWhileRevalidate: EdgeCacheTTL.WEEK,
    },
    i18nKeyFilter: basicSearchPageI18nKeyFilter,
  },
);

SearchPage.AppType = AppType.Basic;

export default SearchPage;

interface ICallbackProps {
  searchParams: string | string[] | undefined;
  store: TBasicStore;
  context: GetServerSidePropsContext;
  initialAppState: ReturnType<typeof setInitialBasicAppState>;
}

const searchResultPageGSSCallback = async ({
  searchParams,
  context,
  store,
  initialAppState,
}: ICallbackProps) => {
  const { market, pathname } = initialAppState;
  const redirect = getSearchRedirect(market, searchParams as string[]);
  if (redirect) {
    return redirect;
  }

  const slug = searchParams?.[0]
    ? (searchParams[0].substring('wedding-'.length) as UrlFriendlySlug)
    : 'venues';
  const area = searchParams?.[1] || 'uk';
  const facets = (searchParams || []).slice(2) as string[];
  const searchQuery: ISearchQuery = {
    slug,
    area,
    facets,
    sort: 'seoScore',
    ...context.query,
  };

  // Check if given supplier type is supported by this routing country
  const supplierType = mapUrlSlugToSupplierType(slug);
  if (!(supplierType && isSupplierTypeSupported(market, supplierType))) {
    return {
      notFound: true,
      props: {},
    };
  }

  // Set canonical link specific for search pages
  const canonical = getCanonicalUrl(slug, area, undefined, market, facets);
  store.dispatch(setCanonicalLink(canonical));

  // LIVE-21319 no need to list alt links if no alternative given
  if (market.locales.length > 1) {
    const alternate = getAlternateLinksForSearch(pathname, market);
    store.dispatch(setAlternateLinks(alternate));
  }

  const initialProps = await fetchInitialProps({ ctx: context, store, searchQuery, market });
  return {
    props: {
      ...initialProps,
      facetsParams: facets,
    },
  };
};

const searchLandingPageGSSCallback = async ({
  initialAppState,
  store,
  searchParams,
}: ICallbackProps) => {
  const { market, pathname } = initialAppState;
  const slug = searchParams?.[0]
    ? (searchParams[0].substring('wedding-'.length) as UrlFriendlySlug)
    : 'venues';

  const [articlesSupport, supplierData] = await Promise.all([
    getArticlesMarketSupport(market.prefix),
    fetchSearchSuppliers(market.country, slug),
  ]);

  store.dispatch(setArticlesSupport(articlesSupport));
  const specific = await getSuppliersLandingPageTexts({ slug, market });
  const generic = supplierData.generic || {
    cities: [],
    counties: {},
    regions: [],
  };
  const typeSpecific = supplierData.typeSpecific;

  // Check if given supplier type is supported by this routing country
  const supplierType = mapUrlSlugToSupplierType(slug);
  if (!(supplierType && isSupplierTypeSupported(market, supplierType))) {
    return {
      notFound: true,
      props: {},
    };
  }

  const alternate = getAlternateLinksForSearchLanding({ pathname, supplierType });
  store.dispatch(setAlternateLinks(alternate));
  const { cities, counties, types } = await filterSearchLandingItems(
    supplierType || 'venue',
    market,
    {
      cities: generic?.cities || [],
      counties: generic?.counties || {},
      types: specific.types || [],
    },
  );

  generic.cities = cities;
  generic.counties = counties;
  specific.types = types;

  const topCountiesPerCountry = await getTopCountiesPerCountry(
    generic.counties,
    market,
    supplierType || 'venue',
  );

  const topTypes = await getTopTypes(specific.types || [], market, supplierType || 'venue');

  return {
    props: {
      generic,
      specific,
      typeSpecific,
      slug,
      topCountiesPerCountry,
      topTypes,
    },
  };
};

const getIsSearchResultsPage = (params?: string[] | string | undefined) =>
  Array.isArray(params) && params.length > 1;

const hasErrorPageProps = (props: any): props is ErrorPageProps => props.error || props.notFound;
